/**
 * ErrorBoundary — Fängt Render-Fehler in Kind-Komponenten ab.
 *
 * Features:
 * - Zeigt Fehlermeldung statt weiße Seite
 * - "Erneut versuchen" setzt den State zurück (ohne Page-Reload)
 * - Auto-Reset bei Route-Wechsel (resetKey prop)
 * - Loggt Fehler in die Konsole
 *
 * @module components/ui/ErrorBoundary
 */

import React from 'react';
import Icon from './Icon.jsx';
import { logError } from '../../lib/monitoring.js';

interface ErrorBoundaryProps {
  resetKey?: string;
  children: React.ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error): void {
    logError(error, 'ErrorBoundary');
  }

  componentDidUpdate(prevProps: ErrorBoundaryProps): void {
    if (this.state.hasError && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ hasError: false, error: null });
    }
  }

  render(): React.ReactNode {
    if (!this.state.hasError) return this.props.children;

    const errMsg = this.state.error?.message;
    const retry = (): void => this.setState({ hasError: false, error: null });
    const reload = (): void => window.location.reload();

    return (
      <div
        style={{
          padding: '60px 24px',
          textAlign: 'center',
          fontFamily: "'DM Sans',sans-serif",
          maxWidth: 480,
          margin: '0 auto',
        }}>
        <div
          style={{
            width: 64,
            height: 64,
            borderRadius: '50%',
            background: 'rgba(239,68,68,.08)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 20px',
          }}>
          <Icon name="warning" size={32} color="#ef4444" />
        </div>
        <h2
          style={{ fontSize: 20, fontWeight: 800, marginBottom: 8, color: 'var(--text, #111)' }}>
          Etwas ist schiefgelaufen
        </h2>
        <p
          style={{ color: 'var(--muted, #888)', marginBottom: 24, fontSize: 14, lineHeight: 1.6 }}>
          Ein unerwarteter Fehler ist aufgetreten. Bitte versuche es erneut.
        </p>
        {errMsg &&
          <details
            style={{
              marginBottom: 24,
              textAlign: 'left',
              background: 'var(--surface2, #f8f8f8)',
              borderRadius: 8,
              padding: '8px 12px',
              fontSize: 12,
              color: 'var(--muted, #888)',
            }}>
            <summary style={{ cursor: 'pointer', fontWeight: 600, fontSize: 11 }}>
              Technische Details
            </summary>
            <pre
              style={{ marginTop: 8, whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: 11, color: '#ef4444' }}>
              {errMsg}
            </pre>
          </details>}
        <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
          <button
            onClick={retry}
            style={{
              background: 'linear-gradient(135deg,#15803d,#22c55e)',
              color: '#fff',
              border: 'none',
              borderRadius: 10,
              padding: '11px 24px',
              cursor: 'pointer',
              fontSize: 14,
              fontWeight: 700,
              display: 'flex',
              alignItems: 'center',
              gap: 6,
            }}>
            <Icon name="refresh" size={14} color="#fff" />
            Erneut versuchen
          </button>
          <button
            onClick={reload}
            style={{
              background: 'transparent',
              color: 'var(--muted, #888)',
              border: '1px solid var(--border, #ddd)',
              borderRadius: 10,
              padding: '11px 24px',
              cursor: 'pointer',
              fontSize: 14,
              fontWeight: 600,
            }}>
            Seite neu laden
          </button>
        </div>
      </div>
    );
  }
}
